// src/pages/ArticleDetail.jsx
// Further refined by Dieter Rams & Jony Ive for a perfectly centered reading experience.

import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axiosClient from '../api/axiosClient';

const fetchArticle = async (doc_reference) => {
  const { data } = await axiosClient.get(`/articles/${doc_reference}`);
  return data;
};

const fetchRelated = async (doc_reference) => {
  const { data } = await axiosClient.get(`/articles/${doc_reference}/similar`);
  return data;
};

const ArticleDetail = () => {
  const { doc_reference } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backQuery = location.state?.query || '';

  // Fetch main article
  const {
    data: article,
    isLoading: loadingArticle,
    error: errorArticle,
  } = useQuery(['article', doc_reference], () => fetchArticle(doc_reference), {
    staleTime: 60000,
  });

  // Fetch related articles
  const {
    data: related,
    isLoading: loadingRelated,
    error: errorRelated,
  } = useQuery(['relatedArticles', doc_reference], () => fetchRelated(doc_reference), {
    staleTime: 60000,
  });

  if (loadingArticle) {
    return <p className="p-6 text-center text-green-600">Loading article...</p>;
  }
  if (errorArticle) {
    return <p className="p-6 text-center text-red-500">Error fetching the article.</p>;
  }
  if (!article) {
    return <p className="p-6 text-center">Article not found.</p>;
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-green-50 to-white py-10">
      {/* Full-width gradient container */}
      <div className="max-w-3xl mx-auto px-6">
        {/* Centered content container */}
        <div>
          {/* Back Button: returns to search page with preserved query */}
          <button
            className="mb-4 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors focus:outline-none"
            onClick={() => navigate(`/search?q=${backQuery}`)}
          >
            &larr; Back
          </button>
          {/* Main article content */}
          <article className="bg-white p-8 rounded-lg shadow mt-4">
            {/* Center the headline */}
            <h1 className="text-3xl font-bold text-green-800 mb-6 text-center">
              {article.title}
            </h1>
            {/* Justify or left-align the text. For a truly centered paragraph, replace text-justify with text-center. */}
            <p className="text-gray-700 leading-relaxed text-lg text-justify">
              {article.content}
            </p>
          </article>
          {/* Related Articles */}
          <section className="mt-10">
            <h2 className="text-2xl font-semibold text-green-700 mb-6 text-center">
              Related Articles
            </h2>
            {loadingRelated ? (
              <p className="text-center text-green-600">Loading related articles...</p>
            ) : errorRelated ? (
              <p className="text-center text-red-500">Error loading related articles.</p>
            ) : related && related.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {related.map((rel) => (
                  <div
                    key={rel.doc_reference}
                    className="p-4 bg-white border border-green-200 rounded-lg shadow hover:shadow-xl cursor-pointer transition-all"
                    onClick={() =>
                      navigate(`/article/${rel.doc_reference}`, {
                        state: { query: backQuery },
                      })
                    }
                  >
                    <h3 className="text-xl font-semibold text-green-800">{rel.title}</h3>
                    <p className="mt-2 text-gray-700">{rel.description}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-600">No related articles found.</p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default ArticleDetail;
